import {
    forwardRef,
    useCallback,
    useMemo,
    useState,
    type ChangeEventHandler,
    type Dispatch,
    type SetStateAction,
    type Ref,
} from 'react';
import Combobox, { type ComboboxProps } from '@ovpn/ui/combobox';
import { useUpdateEffect, useTWDB } from '@ovpn/ui/hooks';
import Label, { type LabelOwnProps } from '@ovpn/ui/label';
import { autoPlacement } from '@ovpn/ui/dropdown';
import Highlighter from '@ovpn/ui/highlighter';
import identity from 'lodash/identity';
import Listbox from '@ovpn/ui/listbox';
import Text from '@ovpn/ui/text';

import { boxed } from '@sso/shared/hocs';

type UseSelectOptions = {
    parseValue?(listboxValue: string): string | undefined;
    onChange?: (value?: string) => void;
    clearQuery?: boolean;
    autoClose?: boolean;
    value?: string;
};

type UseSelect = {
    open: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    listboxValue: string | undefined;
    comboboxValue: string;
    select(id: string | undefined): void;
    selected(id: string): boolean;
    query: string;
    search: ChangeEventHandler<HTMLInputElement>;
    clear(): void;
};

const useSelect = (options = {} as UseSelectOptions): UseSelect => {
    const {
        parseValue = identity,
        clearQuery = false,
        autoClose = true,
        value = '',
        onChange,
    } = options;

    const [open, toggle] = useState<UseSelect['open']>(false);

    const [query, setQuery] = useState<UseSelect['query']>('');

    const [listboxValue, setListboxValue] = useTWDB<UseSelect['listboxValue']>(value);

    const [comboboxValue, setComboboxValue] = useTWDB<UseSelect['comboboxValue']>(
        parseValue(value) || '',
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies:
    const search = useCallback<UseSelect['search']>(event => {
        setQuery(event.target.value);
        setComboboxValue(event.target.value);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies:
    const select = useCallback<UseSelect['select']>(
        id => {
            if (clearQuery) {
                setQuery('');
            }

            setComboboxValue((id ? parseValue(id) : '') || '');
            setListboxValue(id);
            onChange?.(id);

            if (autoClose) {
                toggle(false);
            }
        },
        [onChange],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies:
    const clear = useCallback<UseSelect['clear']>(() => {
        setListboxValue(undefined);
        onChange?.(undefined);
        setComboboxValue('');
        setQuery('');
    }, [onChange]);

    const selected = useCallback<UseSelect['selected']>(id => id === listboxValue, [listboxValue]);

    useUpdateEffect(() => {
        if (!open) {
            if (!listboxValue) {
                setQuery('');
                setComboboxValue('');
            } else if (comboboxValue !== listboxValue) {
                const parsedValue = parseValue(listboxValue) || '';

                if (!clearQuery) {
                    setQuery(parsedValue);
                }

                setComboboxValue(parsedValue);
            }
        }
    }, [open]);

    return {
        open,
        toggle,
        listboxValue,
        comboboxValue,
        select,
        query,
        search,
        clear,
        selected,
    };
};

type SelectProps = Omit<ComboboxProps, 'children'> &
    Omit<LabelOwnProps, 'children' | 'as'> & {
        options: { id: string; title: string }[];
        onChange?: (id?: string) => void;
        searchable?: boolean;
        invalid?: boolean;
        value?: string;
    };

const middlewares = [autoPlacement({ allowedPlacements: ['top', 'bottom'] })];

function Select(
    {
        description,
        appearance,
        className,
        layout,
        title,
        size,
        dir,

        searchable,
        onChange,
        options,
        value,

        ...inputProps
    }: SelectProps,
    ref: Ref<HTMLDivElement>,
) {
    const parseValue = useCallback(
        (id?: string) => options.find(option => option.id === id)?.title,
        [options],
    );

    const { open, toggle, listboxValue, comboboxValue, selected, select, query, search } =
        useSelect({
            clearQuery: true,
            parseValue,
            onChange,
            value,
        });

    const filteredOptions = useMemo(
        () =>
            searchable
                ? options.filter(option => option.title.toLowerCase().includes(query.toLowerCase()))
                : options,
        [searchable, options, query],
    );

    const listboxId = `${inputProps.id}-listbox`;

    return (
        <Label
            appearance={inputProps.invalid ? 'warning' : appearance}
            required={inputProps.required}
            data-testid={inputProps.id}
            description={description}
            className={className}
            layout={layout}
            title={title}
            size={size}
            dir={dir}
        >
            <Combobox
                placeholder={searchable ? parseValue(listboxValue) : undefined}
                query={searchable ? comboboxValue : undefined}
                value={searchable ? undefined : comboboxValue}
                onSearch={searchable ? search : undefined}
                aria-activedescendant={listboxValue}
                aria-controls={listboxId}
                middlewares={middlewares}
                onChange={toggle}
                open={open}
                ref={ref}
                {...inputProps}
            >
                {filteredOptions.length > 0 ? (
                    <Listbox onChange={select} value={selected} id={listboxId}>
                        {filteredOptions.map(option => (
                            <Listbox.Option key={option.id} id={option.id}>
                                <Highlighter autoEscape query={query}>
                                    {option.title}
                                </Highlighter>
                            </Listbox.Option>
                        ))}
                    </Listbox>
                ) : (
                    <Text
                        style={{ textAlign: 'center', padding: '8px 0' }}
                        weight="medium"
                        size="textSm"
                    >
                        No results found
                    </Text>
                )}
            </Combobox>
        </Label>
    );
}

export default boxed(forwardRef(Select));
